import { useLocation, useNavigate } from '@tanstack/react-router'
import { Maximize2 } from 'lucide-react'
import { useEffect, useState } from 'react'
import { createPortal } from 'react-dom'
import { apiBaseUrl } from '../lib/api.js'

/**
 * Loads the ProactiveSite support assistant into the back office.
 *
 * Renders nothing itself. Their loader draws its own launcher button and, once
 * the embed has confirmed the signed-in user is on the assistant's allowlist,
 * their own panel. Since loader v7 that launcher is draggable on its own and
 * remembers where it was left, so the drag code that used to live here is
 * gone — wiring it as well would move the button twice per pointer event.
 *
 * ExpandAffordance below depends on an undocumented loader internal (the panel
 * is marked `data-proactivesite-root`): it
 * floats a button beside `[data-proactivesite-root]` while it is open, which
 * routes to /support-chat (see routes/support-chat.tsx) — a full-page view of
 * the same panel, stretched over the page via the `data-mv-full-page` CSS in
 * app.css. Re-check that page and stylesheet block too before bumping the pin.
 *
 * The signature comes from GET /support/widget-identity rather than from a
 * template, because this app is a static build with no server render. See that
 * route for what the signature is and why it exists.
 *
 * Failure is silent by design: the thing that reports bugs must not be capable
 * of causing one. Every path out of here leaves the portal exactly as it was.
 */

/** The version of their loader this app is pinned to. See ProactiveSite's
 *  widget-loader.ts — v6 is the first that re-reads page context on each open,
 *  which a single-page app needs, and the first with the admin gate; v7 draws a
 *  draggable launcher. */
const LOADER_VERSION = 7
const WIDGET_ORIGIN = 'https://proactivesite.com'

// Module scope rather than a ref: the loader installs global hooks (console.error,
// history) and cannot be torn down, so it must run at most once per page load.
// This survives StrictMode's double-mount in development and any future
// remounting of the shell; a real sign-out is a page load and clears it.
let injected = false

/** The loader's public API surface (window.ProactiveSite), typed here once
 *  rather than re-cast at every call site. `open`/`toggle` take an optional
 *  element to anchor the panel above; without one, open() falls back to the
 *  bottom-right corner. */
export interface ProactiveSiteApi {
  open?: (el?: Element) => void
  close?: () => void
  toggle?: (el?: Element) => void
}

export function getProactiveSite(): ProactiveSiteApi | undefined {
  return (window as { ProactiveSite?: ProactiveSiteApi }).ProactiveSite
}

export function SupportWidget() {
  useEffect(() => {
    if (injected) return
    let cancelled = false

    void (async () => {
      try {
        const res = await fetch(`${apiBaseUrl}/support/widget-identity`, {
          credentials: 'include',
        })
        if (!res.ok) return // 401 on a session that expired mid-page: no widget
        const data = (await res.json()) as {
          enabled?: boolean
          key?: string
          identity?: { id: string; name: string; email: string }
          ts?: string
          sig?: string
        }
        if (cancelled || injected) return
        if (!data.enabled || !data.key || !data.identity || !data.ts || !data.sig) return

        injected = true
        const script = document.createElement('script')
        script.src = `${WIDGET_ORIGIN}/w.js?v=${LOADER_VERSION}`
        script.async = true
        // The loader reads these off document.currentScript, which is set for a
        // dynamically inserted classic script while it executes.
        script.setAttribute('data-key', data.key)
        script.setAttribute('data-user', JSON.stringify(data.identity))
        script.setAttribute('data-ts', data.ts)
        script.setAttribute('data-sig', data.sig)
        document.head.appendChild(script)

      } catch {
        // Offline, blocked by an extension, their origin down — none of which
        // are this application's problem to surface.
      }
    })()

    return () => {
      cancelled = true
    }
  }, [])

  return <ExpandAffordance />
}

interface ExpandButtonPosition {
  left: number
  top: number
}

/**
 * A small button that floats just outside the panel's top-left corner while
 * it's open, linking to /support-chat — the same panel, expanded to fill the
 * page. Top-left, not top-right: the embed draws its own close button in the
 * panel's top-right corner and this must not sit on top of it.
 *
 * Like everything else in this file, a nicety: every path here is wrapped, and
 * failing to find or track the panel just means the button doesn't appear —
 * the widget behaves exactly as it did before this existed.
 */
function ExpandAffordance() {
  const location = useLocation()
  const navigate = useNavigate()
  const [pos, setPos] = useState<ExpandButtonPosition | null>(null)

  useEffect(() => {
    let raf = 0
    let bodyObserver: MutationObserver | null = null
    let styleObserver: MutationObserver | null = null
    let watchedHost: HTMLElement | null = null

    const measure = () => {
      try {
        const host = document.querySelector('[data-proactivesite-root]')
        if (
          !(host instanceof HTMLElement) ||
          host.style.display !== 'block' ||
          host.hasAttribute('data-mv-full-page')
        ) {
          setPos(null)
          return
        }
        const r = host.getBoundingClientRect()
        if (r.width === 0) {
          setPos(null)
          return
        }
        setPos({ left: r.left, top: Math.max(6, r.top - 34) })
      } catch {
        setPos(null)
      }
    }

    const scheduleMeasure = () => {
      cancelAnimationFrame(raf)
      raf = requestAnimationFrame(measure)
    }

    const watchHost = (host: HTMLElement) => {
      if (watchedHost === host) return
      styleObserver?.disconnect()
      watchedHost = host
      styleObserver = new MutationObserver(scheduleMeasure)
      styleObserver.observe(host, { attributes: true, attributeFilter: ['style'] })
    }

    try {
      // The panel host is built lazily (on first open) and can be torn down
      // again if the vendor's admin gate later says "not an administrator" —
      // watch for both. Once it exists, its own style attribute is what
      // changes on open/close/drag/resize.
      bodyObserver = new MutationObserver(() => {
        const host = document.querySelector('[data-proactivesite-root]')
        if (host instanceof HTMLElement) watchHost(host)
        scheduleMeasure()
      })
      bodyObserver.observe(document.body, { childList: true })

      const existing = document.querySelector('[data-proactivesite-root]')
      if (existing instanceof HTMLElement) watchHost(existing)

      window.addEventListener('resize', scheduleMeasure)
    } catch {
      // ignore
    }

    return () => {
      cancelAnimationFrame(raf)
      bodyObserver?.disconnect()
      styleObserver?.disconnect()
      window.removeEventListener('resize', scheduleMeasure)
    }
  }, [])

  if (!pos || location.pathname === '/support-chat') return null

  return createPortal(
    <button
      type="button"
      aria-label="Open chat as a full page"
      title="Open chat as a full page"
      onClick={() => void navigate({ to: '/support-chat', search: { from: location.href } })}
      style={{ position: 'fixed', left: pos.left, top: pos.top, zIndex: 2147483001 }}
      className="flex size-7 items-center justify-center rounded-full border border-border bg-surface text-foreground-muted shadow-md hover:text-foreground"
    >
      <Maximize2 className="size-3.5" aria-hidden="true" />
    </button>,
    document.body,
  )
}
